import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileSpreadsheet, ArrowLeft, Calculator, CheckCircle2, 
  ShieldCheck, Clock, Banknote, TrendingUp, AlertTriangle, FileText, Check, CalendarCheck, MessageSquareMore, Scale
} from 'lucide-react';

// --- IMÁGENES DEL CARRUSEL ---
const NOMINA_CAROUSEL_IMAGES = [
  'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=800&q=80',
  'https://zaphy.com.co/wp-content/uploads/2026/01/nomina.jpg.png'
];

const Nomina = ({ language }) => { 
  
  const navigate = useNavigate();
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % NOMINA_CAROUSEL_IMAGES.length);
    }, 4000); 
    return () => clearInterval(interval);
  }, []);

  // --- DICCIONARIO DE TRADUCCIONES OPTIMIZADO (ALEX HORMOZI + SEO DE EXTREMA DENSIDAD) ---
  const t = {
    es: {
      back: 'Volver al Inicio',
      heroTag: 'Outsourcing de Nómina y Seguridad Social',
      heroTitle: 'Liquidación Electrónica de Nómina:',
      heroSubtitle: 'Precisión Absoluta y Cero Multas',
      heroDesc: 'Delega la carga operativa de liquidar nómina y seguridad social. Evita sanciones de la UGPP y garantiza pagos puntuales a tu equipo.',
      heroBtnQuote: 'Cotizar Nómina',
      heroBtnWhatsApp: 'Escríbenos por WhatsApp',
      imgBadgeTitle: 'Gestión Contable',
      imgBadgeDesc: 'Asumida por Zaphy',
      painTag: '¿Sabías que?',
      painTitle: 'Un error en la nómina puede costar millones',
      painDesc: 'La normativa laboral cambia constantemente. Calcular mal una hora extra, una incapacidad o la seguridad social no solo genera descontento en tu equipo, sino que abre la puerta a sanciones graves de la UGPP y demandas laborales.',
      painHighlight: 'Nosotros no somos un software pasivo donde tú haces el trabajo. En Zaphy nos convertimos en tu departamento de nómina y asumimos la responsabilidad técnica de cada cifra.',
      painBadgeTitle: 'Patrimonio',
      painBadgeDesc: '100% Blindado ante UGPP',
      
      // Banner Estratégico de Traspaso Operativo
      bannerTitle: 'Tercerización Completa sin Desgaste Operativo',
      bannerDesc: 'Al delegar tu nómina con Zaphy, te liberas por completo de las actualizaciones de software, el cargue manual en operadores PILA y el envío de reportes XML ante la DIAN. Nos encargamos de todo el ciclo prestacional y respondemos por la exactitud del cálculo.',
      
      gridTitle: '¿Qué incluye nuestro servicio integral?',
      gridSubtitle: 'Más que un software, somos un equipo humano y contable auditando cada cifra.',
      cards: [
        { title: 'Liquidación Exacta de Salarios', desc: 'Calculamos salarios base, horas extras, recargos nocturnos, dominicales y festivos de manera milimétrica según la ley laboral vigente.' },
        { title: 'Seguridad Social (Planilla PILA)', desc: 'Efectuamos la liquidación, validación y dispersión de aportes obligatorios a EPS, Fondos de Pensión (AFP), Administradoras de Riesgos (ARL) y Cajas de Compensación (CCF).' },
        { title: 'Provisiones y Prestaciones', desc: 'Cálculo consolidado y provisión mensual de primas de servicios, cesantías, intereses a las cesantías and vacaciones para evitar descuadres financieros.' },
        { title: 'Control de Novedades y Ausentismos', desc: 'Procesamos y reportamos de forma oportuna incapacidades generales, licencias de maternidad o paternidad, permisos y ausentismos laborales.' },
        { title: 'Nómina Electrónica ante la DIAN', desc: 'Sincronizamos y transmitimos mensualmente los archivos XML de nómina electrónica exigidos por la DIAN, convirtiendo tus costos en deducibles.' },
        { title: 'Blindaje Legal ante la UGPP', desc: 'Estructuramos tus bases de cotización bajo el artículo 30 de la Ley 1393 para proteger tu PyME ante requerimientos o auditorías sorpresivas de la UGPP.' },
        { title: 'Liquidaciones de Contrato Definitivas', desc: 'Redactamos y calculamos las actas de liquidación final por mutuo acuerdo, renuncia o despido, incluyendo indemnizaciones de ley si aplica, mitigando riesgos de demandas.' },
        { title: 'Reportería y Desprendibles', desc: 'Te entregamos archivos listos para tu interfaz contable y despachamos los desprendibles de pago firmados de forma automática al email de cada colaborador.' }
      ],
      seoTitle: 'Preguntas Frecuentes sobre la Tercerización de Nómina en Colombia',
      seoSubtitle: 'Todo lo que necesitas saber sobre el cumplimiento normativo, la UGPP y el soporte contable de Zaphy.',
      faqs: [
        { q: '¿Qué ventajas tiene contratar una empresa de outsourcing de nómina?', a: 'Te permite transferir la responsabilidad operativa y el riesgo de errores de cálculo. Reduces costos fijos en software premium de nómina y personal interno, garantizando que un equipo de expertos mantenga tu contabilidad blindada bajo la legislación colombiana vigente.' },
        { q: '¿Cómo nos protege Zaphy ante una fiscalización de la UGPP?', a: 'Realizamos auditorías recurrentes a cada planilla PILA y base salarial. Al delegar tu operación con Zaphy, cuentas con un soporte técnico y legal robusto que asegura que cada recargo, hora extra y provisión esté perfectamente calculada, mitigando cualquier riesgo de sanción administrativa.' },
        { q: '¿El servicio incluye el envío de desprendibles y reportes a la DIAN?', a: 'Sí, emitimos de forma correcta la nómina electrónica obligatoria ante la DIAN y nos encargamos de despachar los desprendibles de pago firmados directamente a los correos electrónicos corporativos de tus colaboradores.' },
        { q: '¿Qué pasa si tengo un empleado con novedades complejas a mitad de mes?', a: 'Nuestro canal de atención está abierto de forma permanente para procesar novedades sobre la marcha. Simplemente nos reportas la novedad (incapacidad, licencia, variación de salario) y nuestro equipo la incorpora en la pre-liquidación asegurando que los aportes a seguridad social no sufran descuadres.' },
        { q: '¿Ofrecen asesoría jurídica en caso de requerir un despido o un proceso disciplinario?', a: '¡Totalmente! Como parte de nuestra gestión de relaciones laborales y portafolio jurídico integral, te asesoramos en la estructuración de contratos de trabajo y te brindamos el acompañamiento legal necesario para realizar descargos o desvinculaciones safely.' }
      ],
      ctaTitle: '¿Cuánto cuesta tu tranquilidad?',
      ctaDesc: 'Nuestros planes se ajustan al número de empleados. Solicita una cotización en tiempo real o agenda una asesoría personalizada.',
      ctaBtnQuote: 'Cotizar Servicio',
      ctaBtnSchedule: 'Agendar Asesoría',
      ctaBtnWhatsApp: 'Hablemos por WhatsApp'
    },
    en: {
      back: 'Back to Home',
      heroTag: 'Payroll and Social Security Outsourcing',
      heroTitle: 'Electronic Payroll Liquidation:',
      heroSubtitle: 'Absolute Precision and Zero Fines',
      heroDesc: 'Delegate the operational burden of payroll and social security liquidation. Avoid UGPP sanctions and ensure timely payments to your team.',
      heroBtnQuote: 'Quote Payroll',
      heroBtnWhatsApp: 'Chat on WhatsApp',
      imgBadgeTitle: 'Accounting',
      imgBadgeDesc: 'Managed by Zaphy',
      painTag: 'Did you know?',
      painTitle: 'A payroll error can cost millions',
      painDesc: 'Labor regulations change constantly. Miscalculating overtime, disability, or social security not only creates discontent in your team but opens the door to serious UGPP sanctions and labor lawsuits.',
      painHighlight: 'We are not a passive software where you do the hard work. At Zaphy, we become your payroll department and take technical responsibility for each figure.',
      painBadgeTitle: 'Company Assets',
      painBadgeDesc: '100% Shielded against UGPP',
      
      bannerTitle: 'Full Outsourcing with Zero Operational Friction',
      bannerDesc: 'By outsourcing your payroll with Zaphy, you completely free yourself from software updates, manual entries into PILA operators, and XML reporting to DIAN. We manage the entire benefit cycle and stand responsible for the calculation precision.',
      
      gridTitle: 'What does our comprehensive service include?',
      gridSubtitle: 'More than software, we are a human and accounting team auditing every single figure.',
      cards: [
        { title: 'Exact Salary Liquidation', desc: 'We calculate base salaries, overtime, night surcharges, Sundays, and holidays with precision according to current labor law.' },
        { title: 'Social Security (PILA Form)', desc: 'We handle the liquidation, validation, and payment dispersion for mandatory contributions to EPS, Pension Funds (AFP), Risk Managers (ARL), and Compensation Funds (CCF).' },
        { title: 'Provisions and Benefits', desc: 'Consolidated calculation and monthly provision for service bonuses, severance, interest on severance, and vacations to avoid financial gaps.' },
        { title: 'Leave and Absence Management', desc: 'We process and timely report general disabilities, maternity or paternity leaves, permits, and labor absenteeism.' },
        { title: 'Electronic Payroll before DIAN', desc: 'We synchronize and monthly transmit the mandatory electronic payroll XML files required by DIAN, ensuring the deductibility of your costs.' },
        { title: 'Legal Shielding against UGPP', desc: 'We structure your contribution bases under article 30 of Law 1393 to protect your PyME against surprise UGPP requests or audits.' },
        { title: 'Final Contract Liquidations', desc: 'We draft and calculate final settlement acts due to mutual agreement, resignation, or termination, including legal severance if applicable, mitigating lawsuit risks.' },
        { title: 'Reporting and Pay Stubs', desc: 'We deliver ready-to-use files for your accounting interface and automatically dispatch signed pay stubs to each employee corporate email.' }
      ],
      seoTitle: 'Frequently Asked Questions about Payroll Outsourcing in Colombia',
      seoSubtitle: 'Everything you need to know about regulatory compliance, UGPP, and Zaphys accounting support.',
      faqs: [
        { q: 'What are the advantages of hiring a payroll outsourcing company?', a: 'It allows you to transfer operational responsibility and the risk of calculation errors. You reduce fixed costs in premium payroll software and internal staff, ensuring that a team of experts keeps your accounting shielded under current Colombian legislation.' },
        { q: 'How does Zaphy protect us against a UGPP audit?', a: 'We perform recurring audits on each PILA form and salary base. By delegating your operation with Zaphy, you have robust technical and legal support that ensures each surcharge, overtime, and provision is perfectly calculated, mitigating any risk of administrative sanctions.' },
        { q: 'Does the service include sending stubs and reports to the DIAN?', a: 'Yes, we correctly issue the mandatory electronic payroll to the DIAN and take care of dispatching the signed payment stubs directly to your employees corporate emails.' },
        { q: 'What happens if I have an employee with complex changes in the middle of the month?', a: 'Our customer service channel is permanently open to process updates on the go. You simply report the change to us (disability, leave, salary variation) and our team incorporates it into the pre-liquidation, ensuring social security contributions remain aligned.' },
        { q: 'Do you offer legal advice in case a termination or a disciplinary process is needed?', a: 'Absolutely! As part of our labor relations and comprehensive legal portfolio, we advise you on structuring employment contracts and provide the necessary legal support to carry out warnings, hearings, or terminations safely.' }
      ],
      ctaTitle: 'How much is your peace of mind worth?',
      ctaDesc: 'Our plans adjust to the number of employees. Request a real-time quote or schedule a personalized advisory session.',
      ctaBtnQuote: 'Quote Service',
      ctaBtnSchedule: 'Schedule Advisory',
      ctaBtnWhatsApp: 'Chat via WhatsApp'
    }
  };

  const text = t[language] || t.es;

  const handleWhatsAppRedirect = () => {
    const targetUrl = `https://api.whatsapp.com/send?phone=573114044203&text=${encodeURIComponent("¡Hola, Zaphy! 🚀 Estuve revisando el servicio de Outsourcing de Nómina en su web y quiero solicitar información de tarifas para mi PyME de forma directa.")}`;
    window.open(targetUrl, '_blank');
  };

  return (
    <div className="animate-[fadeIn_0.5s_ease-out] pt-32 pb-20 min-h-screen bg-gray-50 font-[Poppins]">
      
      {/* --- 1. HERO SECTION --- */}
      <div className="relative bg-[#0F172A] text-white py-24 overflow-hidden mb-16">
         <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{backgroundImage: "url('https://www.transparenttextures.com/patterns/cubes.png')"}}></div>
         <div className="absolute inset-0 bg-gradient-to-r from-green-900/90 via-[#0F172A]/90 to-[#0F172A]"></div>
         
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors font-medium">
               <ArrowLeft size={20} /> {text.back}
            </button>
            
            <div className="flex flex-col md:flex-row gap-12 items-center">
                <div className="flex-1">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 rounded-xl bg-green-500/20 text-green-400 border border-green-500/30">
                            <FileSpreadsheet size={40} />
                        </div>
                        <span className="text-green-400 font-bold tracking-widest uppercase text-xs md:text-sm">{text.heroTag}</span>
                    </div>
                    
                    <h1 className="text-3xl sm:text-5xl font-extrabold mb-6 leading-tight">
                    {text.heroTitle} <br/>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-600">{text.heroSubtitle}</span>
                    </h1>
                    
                    <p className="text-xl text-gray-300 max-w-2xl leading-relaxed mb-8">
                    {text.heroDesc}
                    </p>

                    <div className="flex flex-wrap gap-4">
                        <button 
                          onClick={() => navigate('/cotizador')} 
                          className="bg-green-600 text-white font-bold py-4 px-8 rounded-xl shadow-lg shadow-green-900/40 transition-all flex items-center gap-2 transform hover:-translate-y-1 hover:bg-green-700 text-sm uppercase tracking-wide"
                        >
                            <Calculator size={20}/> {text.heroBtnQuote}
                        </button>

                        <button 
                          onClick={handleWhatsAppRedirect} 
                          className="bg-transparent border-2 border-green-500 text-green-400 font-bold py-4 px-8 rounded-xl transition-all flex items-center gap-2 transform hover:-translate-y-1 hover:bg-green-600 hover:text-white shadow-lg shadow-green-500/5 text-sm uppercase tracking-wide"
                        >
                            <MessageSquareMore size={20}/> {text.heroBtnWhatsApp}
                        </button>
                    </div>
                </div>

                <div className="w-full max-w-md mt-10 md:mt-0">
                    <div className="relative group">
                        <div className="absolute -inset-1 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-[#0F172A]">
                             <img 
                                src="https://zaphy.com.co/wp-content/uploads/2026/01/nominaZaphy.png" 
                                alt="Outsourcing de Nómina Electrónica para PyMEs en Colombia" 
                                className="w-full h-auto object-cover transform transition-transform duration-500 group-hover:scale-105"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 via-transparent to-transparent opacity-60"></div>
                            <div className="absolute bottom-4 left-4 bg-white/10 backdrop-blur-md border border-white/20 p-3 rounded-xl flex items-center gap-3">
                                <div className="bg-green-50 p-1.5 rounded-full text-white"><CheckCircle2 size={16} /></div>
                                <div>
                                    <p className="text-xs text-green-200 uppercase font-bold">{text.imgBadgeTitle}</p>
                                    <p className="text-sm font-bold text-white">{text.imgBadgeDesc}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
         </div>
      </div>

      {/* --- 2. EL PROBLEMA --- */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <div className="bg-white rounded-[40px] p-8 md:p-12 shadow-xl border border-gray-100 flex flex-col lg:flex-row items-center gap-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-50 rounded-bl-[100px] z-0"></div>

              <div className="lg:w-1/2 relative z-10">
                  <div className="inline-flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-full font-bold text-sm mb-4 border border-red-100">
                      <AlertTriangle size={16} /> {text.painTag}
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">
                      {text.painTitle}
                  </h2>
                  <p className="text-gray-600 text-lg leading-relaxed mb-6">
                      {text.painDesc}
                  </p>
                  <p className="font-bold text-gray-800 border-l-4 border-purple-600 pl-4">
                      {text.painHighlight}
                  </p>
              </div>

              <div className="w-full lg:w-1/2 relative z-10 mt-12 lg:mt-0">
                  <div className="absolute -inset-4 bg-green-100 rounded-[40px] transform rotate-3 z-0"></div>
                  
                  <div className="relative z-10 rounded-[30px] shadow-lg w-full h-64 sm:h-80 overflow-hidden bg-gray-200">
                      {NOMINA_CAROUSEL_IMAGES.map((img, idx) => (
                          <img 
                            key={idx}
                            src={img} 
                            alt={`Liquidación de Seguridad Social PILA ${idx + 1}`} 
                            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${idx === currentImage ? 'opacity-100' : 'opacity-0'}`}
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/800x600/10B981/FFFFFF?text=Nomina'; }}
                          />
                      ))}
                      
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
                          {NOMINA_CAROUSEL_IMAGES.map((_, idx) => (
                              <div 
                                key={idx} 
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentImage ? 'bg-green-500 w-4' : 'bg-white/50'}`}
                              />
                          ))}
                      </div>
                  </div>

                  <div className="absolute -bottom-6 -left-6 z-20 bg-white p-4 rounded-2xl shadow-xl border border-gray-100 flex items-center gap-3">
                      <div className="bg-purple-100 p-2 rounded-full text-purple-600"><Check size={24}/></div>
                      <div>
                          <p className="text-xs text-gray-500 font-bold uppercase">{text.painBadgeTitle}</p>
                          <p className="text-sm font-bold text-gray-900">{text.painBadgeDesc}</p>
                      </div>
                  </div>
              </div>
          </div>
      </div>

      {/* --- BANNER ESTRATÉGICO DE TRASPASO OPERATIVO Y CONTROL DIAN/UGPP --- */}
      <div className="max-w-7xl mx-auto px-4 mb-24">
         <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-[30px] p-8 md:p-10 border border-slate-700/50 shadow-xl flex flex-col md:flex-row items-center gap-8">
            <div className="bg-green-500/10 p-4 rounded-2xl text-green-400 shrink-0 border border-green-500/20">
               <Scale size={40} />
            </div>
            <div>
               <h3 className="text-xl md:text-2xl font-bold text-white mb-2">{text.bannerTitle}</h3>
               <p className="text-slate-300 text-sm md:text-base leading-relaxed">{text.bannerDesc}</p>
            </div>
         </div>
      </div>

      {/* --- 3. NUESTRA SOLUCIÓN INTEGRAL --- */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
         <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">{text.gridTitle}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{text.gridSubtitle}</p>
         </div>

         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {text.cards.map((card, idx) => {
               const icons = [Calculator, ShieldCheck, FileText, Clock, TrendingUp, Banknote, AlertTriangle, FileSpreadsheet];
               const Icon = icons[idx];
               const styles = [
                  { bg: 'bg-green-50', text: 'text-green-600', border: 'hover:border-green-400' },
                  { bg: 'bg-blue-50', text: 'text-blue-600', border: 'hover:border-blue-400' },
                  { bg: 'bg-purple-50', text: 'text-purple-600', border: 'hover:border-purple-400' },
                  { bg: 'bg-yellow-50', text: 'text-yellow-600', border: 'hover:border-yellow-400' },
                  { bg: 'bg-red-50', text: 'text-red-600', border: 'hover:border-red-400' },
                  { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'hover:border-indigo-400' },
                  { bg: 'bg-orange-50', text: 'text-orange-600', border: 'hover:border-orange-400' },
                  { bg: 'bg-teal-50', text: 'text-teal-600', border: 'hover:border-teal-400' }
               ];
               const style = styles[idx] || styles[0];

               return (
                  <div key={idx} className={`bg-white p-6 rounded-3xl border border-gray-100 hover:shadow-xl transition-all group ${style.border}`}>
                     <div className={`w-12 h-12 ${style.bg} ${style.text} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                        <Icon size={24} />
                     </div>
                     <h3 className="text-lg font-bold text-gray-900 mb-2">{card.title}</h3>
                     <p className="text-gray-500 text-xs md:text-sm leading-relaxed">
                        {card.desc}
                     </p>
                  </div>
               );
            })}
         </div>
      </div>

      {/* --- 3.5: BLOQUE DE PREGUNTAS FRECUENTES --- */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
         <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">{text.seoTitle}</h2>
            <p className="text-gray-500 text-xs md:text-sm">{text.seoSubtitle}</p>
         </div>
         <div className="space-y-4">
            {text.faqs.map((faq, idx) => (
               <div key={idx} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:border-slate-200 transition-all">
                  <h3 className="text-sm md:text-base font-bold text-gray-950 mb-2 flex items-start gap-2">
                     <span className="text-green-600 shrink-0">❓</span> {faq.q}
                  </h3>
                  <p className="text-gray-600 text-xs md:text-sm leading-relaxed pl-6">{faq.a}</p>
               </div>
            ))}
         </div>
      </div>

      {/* --- 4. CTA FINAL --- */}
      <div className="max-w-4xl mx-auto px-4 text-center">
         <div className="bg-gradient-to-br from-gray-900 to-green-900 rounded-[40px] p-12 text-white shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16"></div>
            
            <h2 className="text-3xl sm:text-5xl font-extrabold mb-6 relative z-10">{text.ctaTitle}</h2>
            <p className="text-green-100 text-base md:text-lg mb-8 max-w-2xl mx-auto relative z-10">
               {text.ctaDesc}
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4 relative z-10">
                <button 
                    onClick={() => navigate('/cotizador')}
                    className="bg-white text-green-900 font-bold py-4 px-8 rounded-xl shadow-lg hover:shadow-green-50 transition-all transform hover:-translate-y-1 relative z-10 flex items-center justify-center gap-2 text-xs md:text-sm uppercase tracking-wide"
                >
                    <Calculator size={20}/>
                    {text.ctaBtnQuote}
                </button>

                <button 
                    onClick={handleWhatsAppRedirect}
                    className="bg-emerald-600 text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:bg-emerald-700 transition-all transform hover:-translate-y-1 relative z-10 flex items-center justify-center gap-2 text-xs md:text-sm uppercase tracking-wide"
                >
                    <MessageSquareMore size={20}/>
                    {text.ctaBtnWhatsApp}
                </button>

                <a 
                    href="https://calendar.app.google/UYZ2ERubo552yRg19"
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-transparent border-2 border-white text-white font-bold py-4 px-8 rounded-xl hover:bg-white/10 transition-all relative z-10 flex items-center justify-center gap-2 text-xs md:text-sm uppercase tracking-wide"
                >
                    <CalendarCheck size={20}/>
                    {text.ctaBtnSchedule}
                </a>
            </div>
         </div>
      </div>

    </div>
  );
};

export default Nomina;